import { memo, useCallback } from 'react';
import { useClientContext } from 'client/context';
import { Strategy } from 'client/voladeum/strategies/types';
import { setAdapters } from 'ducks/adapters/slices';
import { updateModal } from 'ducks/modals/slices';
import { setPairs } from 'ducks/pairs/slices';
import { updateSettings } from 'ducks/settings/slices';
import { setSizingMethods } from 'ducks/sizing-methods/slices';
import { GridCellIconSelect } from 'framework/Grid/cells/GridCell';
import { useGridRowContext } from 'framework/Grid/context/GridRowContext';
import { useConfirmModal } from 'hooks/useConfirmModal';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useAppDispatch } from 'store/hooks';

import { STRATEGY_OPTION } from '../../enums';

import { strategy_options } from './constants';
import { StrategyOption } from './types';

export const GridStrategyEllipsisCell = memo(() => {
  const dispatch = useAppDispatch();
  const { Voladeum, handleError } = useClientContext();
  const { row } = useGridRowContext<Strategy>();
  const { setStrategy } = useStrategyContext();
  const confirmModal = useConfirmModal();

  const onSelect = useCallback(
    async ({ name, message, id }: StrategyOption) => {
      switch (id) {
        case STRATEGY_OPTION.ACTIVATE_STRATEGY:
          confirmModal({
            title: name,
            message: message,
            onConfirm: () => Voladeum.strategies.activate(row.strategy_id),
          });
          break;
        case STRATEGY_OPTION.ARCHIVE_STRATEGY:
          confirmModal({
            title: name,
            message: message,
            onConfirm: () => Voladeum.strategies.archive(row.strategy_id),
          });
          break;
        case STRATEGY_OPTION.UPDATE_STRATEGY:
          try {
            dispatch(updateSettings({ loading: true }));

            const [voladeum_pairs, voladeum_adapters, voladeum_sizing_methods] = await Promise.all([
              Voladeum.pairs.list(),
              Voladeum.adapters.list(),
              Voladeum.sizingMethods.list(),
            ]);

            dispatch(setPairs(voladeum_pairs.data));
            dispatch(setAdapters(voladeum_adapters.data));
            dispatch(setSizingMethods(voladeum_sizing_methods.data));
            setStrategy(row);
            dispatch(
              updateModal({ key: 'strategy_manage', data: { is_open: true, is_update: true } })
            );
            dispatch(updateSettings({ loading: false }));
          } catch (err) {
            handleError(err);
          }
      }
    },
    [
      confirmModal,
      Voladeum.strategies,
      Voladeum.pairs,
      Voladeum.adapters,
      Voladeum.sizingMethods,
      row,
      dispatch,
      setStrategy,
      handleError,
    ]
  );
  return <GridCellIconSelect option_key="name" options={strategy_options} onSelect={onSelect} />;
});
